import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadBlueprints } from 'dashboard/redux/thunk-actions/blueprints';
import {
  loadOrganization,
  updateStripePaymentMethod,
} from 'dashboard/redux/thunk-actions/organization';
import DashboardOrganizationBillingOverviewView from 'dashboard/views/DashboardOrganizationBillingOverviewView/DashboardOrganizationBillingOverviewView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiEndpoint, ApiModel, ApiSingletonPayload } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardOrganizationBillingOverviewRoute = ({
  currentOrganizationPlan,
  blueprints,
  updateStripePaymentMethod,
  loadOrganization,
  loadBlueprints,
  clearMessages,
  showMessage,
}: PropsFromRedux) => {
  const { callback: updateStripePaymentMethodId, error: updateStripePaymentMethodError } =
    useApiCallback(async (paymentMethodId: string) => {
      clearMessages();
      await updateStripePaymentMethod(paymentMethodId);
      showMessage('Billing information successfully updated.', MessageType.Success);
    }, []);

  useApiErrorHandler({
    error: updateStripePaymentMethodError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Billing information failed to update. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { callback: loadAvailablePlans, error: loadAvailablePlansError } =
    useApiCallback(async (): Promise<AvailableOrganizationPlan[]> => {
      const response = await SessionClientDashboard.getResourceMeta<{
        'available-plans': AvailableOrganizationPlan[];
      }>(ApiEndpoint.OrganizationPlan, 'available-plans');

      return response?.meta?.['available-plans'] ?? [];
    }, []);

  useApiErrorHandler({
    error: loadAvailablePlansError,
    onError: () =>
      showMessage(
        'There was an error loading this page. Please try again.',
        MessageType.Failure,
        false,
      ),
    onNetworkError: () =>
      showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleCreatePlanMigrationError, callback: handleCreateOrganizationPlanMigration } =
    useApiCallback(async (inquiryId: string, requestedPlanKey: string) => {
      const response = await SessionClientDashboard.postResourceCreate<
        OrganizationPlanMigration,
        ApiSingletonPayload<{
          'inquiry-id': string;
          'requested-plan-key': string;
        }>
      >({
        type: ApiModel.OrganizationPlanMigration,
        attributes: {
          'inquiry-id': inquiryId,
          'requested-plan-key': requestedPlanKey,
        },
      });

      return response.data;
    }, []);

  useApiErrorHandler({
    error: handleCreatePlanMigrationError,
    onError: () => showMessage('Failed to request new plan.', MessageType.Failure, false),
    onNetworkError: () =>
      showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { callback: handleLoadOrganizationPlanMigration } = useApiCallback(
    async (organizationPlanMigrationId: string) => {
      const response = await SessionClientDashboard.getResource<OrganizationPlanMigration>(
        ApiEndpoint.OrganizationPlanMigration,
        organizationPlanMigrationId,
      );

      return response.data;
    },
    [],
  );

  const openTalkToSalesLink = () => {
    const CONTACT_SALES_PAGE =
      'https://withpersona.com/contact?utm_source=product&utm_medium=referral&utm_audience=a&utm_campaign=acq_gen_ds_general-in-dashboard-plg-pls';

    window.open(`${CONTACT_SALES_PAGE}`, '_blank');
  };

  return (
    <DashboardOrganizationBillingOverviewView
      currentOrganizationPlan={currentOrganizationPlan}
      blueprints={blueprints}
      loadOrganization={loadOrganization}
      loadAvailablePlans={loadAvailablePlans}
      updatePayment={updateStripePaymentMethodId}
      handleCreateOrganizationPlanMigration={handleCreateOrganizationPlanMigration}
      handleLoadOrganizationPlanMigration={handleLoadOrganizationPlanMigration}
      loadBlueprints={loadBlueprints}
      showMessage={showMessage}
      openTalkToSalesLink={openTalkToSalesLink}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentOrganizationPlan: state.organization.currentOrganizationPlan,
  blueprints: state.blueprints.blueprints,
});

const mapDispatchToProps = {
  loadOrganization,
  loadBlueprints,
  updateStripePaymentMethod,
  clearMessages,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardOrganizationBillingOverviewRoute);
